/* eslint-disable import/no-extraneous-dependencies */
import React from 'react';
import './DateLocal.scss';
import Input from '../Input/Input';
import 'react-calendar/dist/Calendar.css';
import { DateCalendar } from '../DateCalendar/DateCalendar';

export enum DateType {
  date = 'date',
  dateTimeLocal = 'datetime-local',
}

interface Props {
  value: Date | undefined
  onChange: (date: Date | undefined, isIncorrect?: boolean) => void
  width: number
  type: DateType
  max?: Date
  min?: Date
  label?: string
  displayStyle?: string | undefined
  disabled?: boolean
  showErrorMessage?: boolean
  error?: boolean
  isClearable?: boolean
}

export const DateLocal = ({ type, max = undefined, min = undefined, value, onChange, width, label = undefined, displayStyle, disabled, showErrorMessage, error, isClearable = true }: Props) => {
  const [inputValue, setInputValue] = React.useState<string | undefined>(value ? type === DateType.date ? value.toStringForInput() : value.toStringForInputWithTime() : '');
  const [isChanged, setIsChanged] = React.useState(false);
  const nodeRef = React.useRef(null);

  // Обновляем состояние inputValue при изменении value извне
  React.useEffect(() => {
    setInputValue(value ? type === DateType.date ? value.toStringForInput() : value.toStringForInputWithTime() : '');
  }, [value]);

  function isValidDate(dateString: string): boolean {
    // Проверяем валидность введенной даты
    const timestamp = Date.parse(dateString);
    if (Number.isNaN(timestamp)) {
      return false;
    }

    if (type === DateType.date) {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return (
        date.getFullYear() === year
        && date.getMonth() === month - 1
        && date.getDate() === day
      );
    }

    return true;
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    const timestamp = Date.parse(e.target.value);
    const dateString = type === DateType.date ? e.target.value : e.target.value.split('T')[0];
    const timeString = type === DateType.dateTimeLocal && e.target.value ? e.target.value.split('T')[1] : undefined;
    const [year, month, day] = dateString.split('-').map(Number);
    const [hours, minutes] = type === DateType.dateTimeLocal && timeString ? timeString!.split(':').map(Number) : [0, 0];

    // Раскомментировать, если потребуется обнулять дату при некорректном вводе
    // if (!e.target.value) {
    //   onChange(undefined, false);
    //   return;
    // }

    const isFullDate = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
    if (type === DateType.dateTimeLocal) {
      const hasTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(e.target.value);
      if (!hasTime) { onChange(value, true); return; }
    } else if (!isFullDate) {
      onChange(value, true);
      return;
    }

    if (Number.isNaN(timestamp) || year < 1900 || year > 2100) {
      // не сбрасываем значение! либо вообще ничего не шлём:
      // return;
      // либо шлём текущее value с флагом ошибки:
      onChange(value, true);
      return;
    }

    // корректный ввод:
    if (type === DateType.dateTimeLocal) {
      const localDate = new Date(year, month - 1, day, hours, minutes);
      onChange(localDate, false);
    } else {
      onChange(new Date(year, month - 1, day, 0, 0, 0), false);
    }
  };

  // Форматируем максимальное и минимальное значения
  const maxValue = max ? type === DateType.date ? max.toStringForInput() : max.toStringForInputWithTime() : undefined;
  const minValue = min ? type === DateType.date ? min.toStringForInput() : min.toStringForInputWithTime() : undefined;

  return (
    <div className="dateLocal" style={{ width: `${isClearable ? width + 20 : width}px` }}>
      {label && (
        <label
          htmlFor={label}
          style={{ color: error ? '#dc3545' : '' }}
        >
          {label}
        </label>)}
      <div className="dateLocalContent">
        <Input
          width={`${isClearable ? width + 20 : width}px`}
          inputType={type}
          value={inputValue}
          maxValue={maxValue}
          minValue={minValue}
          onChange={(e) => handleDateChange(e)}
          warning={error}
          displayStyle={displayStyle}
          disabled={disabled}
          inputRef={nodeRef}
        />
        {isClearable && (
          <span
            className="dateLocalContentRemoveSvg"
            onClick={() => {
              if (!disabled) {
                setInputValue('');
                onChange(undefined, false);
                (nodeRef.current! as HTMLInputElement).value = '';
              }
            }}
          >
            <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="css-tj5bde-Svg">
              <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
            </svg>
          </span>)}
        <DateCalendar
          onChange={(date) => {
            setInputValue((type === DateType.date ? date?.toStringForInput() : date?.toStringForInputWithTime()) ?? '');
            onChange(date, false);
          }}
          value={value}
          type={type}
          disabled={disabled}
          minDate={min}
        />
      </div>
      {showErrorMessage && error && <div className="invalid-feedback">Некорректная дата</div>}
    </div>
  );
};
